import { MutableRefObject, useEffect, useRef } from 'react';
import {
  Formik,
  FormikContextType,
  FormikValues,
  setNestedObjectValues,
} from 'formik';
import { Form as AntForm } from 'fave-ui';

export interface FormProps<T> {
  formRef?: MutableRefObject<FormikContextType<T> | undefined>;
}

const Form = <T extends {}>({
  initialValues,
  onSubmit,
  validationSchema,
  validateOnMount,
  validateOnChange = false,
  validateOnSchemaChange = false,
  children,
  formRef,
  ...rest
}: FormikValues & FormProps<T>) => {
  const ref = useRef<FormikContextType<T>>();

  useEffect(() => {
    if (validateOnSchemaChange) ref.current?.validateForm();
    if (validateOnMount)
      ref.current?.validateForm().then(() =>
        ref.current?.setTouched(
          // set the touched state once validateForm is done
          setNestedObjectValues(ref.current.errors, true),
        ),
      );
  }, [ref, validateOnSchemaChange, validateOnMount]);

  const refPassthrough = (instance: FormikContextType<T>) => {
    ref.current = instance;

    if (formRef) formRef.current = instance;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={validateOnChange}
      validateOnMount
      enableReinitialize
      innerRef={refPassthrough}
      {...rest}
    >
      <AntForm layout="vertical">{children}</AntForm>
    </Formik>
  );
};

export default Form;
